import _ from 'lodash';
import moment from 'moment-timezone';

const formValueConfig = {
  confirmByDate: {
    get() {
      return moment.tz(this.purchaseOrder.confirmBy, this.foodhub.tzid).toDate();
    },
    set(confirmByDate) {
      return {
        confirmBy: moment
          .tz(confirmByDate, this.foodhub.tzid)
          .hour(this.confirmByHour)
          .startOf('hour')
          .toDate(),
      };
    },
    validate(confirmByDate) {
      if (!this.isFieldEdited('confirmByDate')) {
        // Only validate against current time when this field is being changed; otherwise, this
        // validation triggers when making unrelated changes to the PO in the future.
        return null;
      }

      const startOfDay = moment.tz(this.foodhub.tzid).startOf('day');
      const confirmBy = moment
        .tz(confirmByDate, this.foodhub.tzid)
        .hour(this.confirmByHour)
        .startOf('hour')
        .toDate();
      if (confirmByDate == null || moment(confirmBy).isBefore(startOfDay)) {
        return 'Confirmation deadline must be in the future.';
      }
    },
  },
  confirmByHour: {
    get() {
      return moment.tz(this.purchaseOrder.confirmBy, this.foodhub.tzid).hour();
    },
  },
  dropoffDate: {
    get() {
      return moment.tz(this.purchaseOrder.dropoffDay, this.foodhub.tzid).toDate();
    },
    set(dropoffDate) {
      return {dropoffDay: moment.tz(dropoffDate, this.foodhub.tzid).format('YYYY-MM-DD')};
    },
    validate(dropoffDate) {
      if (!this.isFieldEdited('dropoffDate')) {
        // Only validate against current time when this field is being changed; otherwise, this
        // validation triggers when making unrelated changes to the PO in the future.
        return null;
      }

      const startOfDay = moment.tz(this.foodhub.tzid).startOf('day');
      const dropoffDay = moment.tz(dropoffDate, this.foodhub.tzid).startOf('day');
      if (dropoffDate == null || moment(dropoffDay).isBefore(startOfDay)) {
        return 'Delivery day must be in the future.';
      }
    },
  },
  dropoffWindow: {
    get() {
      return this.purchaseOrder.dropoffWindow;
    },
    set(value) {
      return {dropoffWindow: value};
    },
  },
  notesForVendor: {
    get() {
      return this.purchaseOrder.notesForVendor;
    },
    set(value) {
      return {notesForVendor: value};
    },
  },
};

/**
 * Form that manages purchase order inputs
 * - converts purchaseOrder to form data
 * - answers questions and manipulates form data (but does not tell you how to display form data)
 * - assigns form data to purchaseOrder
 */
export default class PurchaseOrderForm {
  constructor(purchaseOrder, {foodhub, $injector}) {
    this.purchaseOrder = purchaseOrder;
    this.foodhub = foodhub;
    this.Promise = $injector.get('Promise');
    this._mapPurchaseOrderToForm();
  }

  _mapPurchaseOrderToForm() {
    const form = {};
    // eslint-disable-next-line guard-for-in
    for (const formValue in formValueConfig) {
      const config = formValueConfig[formValue];
      form[formValue] = config.get.call(this);
    }
    this._originalFormData = form;
    _.assign(this, form);
    this.confirmByHourOptions = [9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21].map((i) => ({
      value: i,
      label: moment
        .tz(this.purchaseOrder.confirmBy, this.foodhub.tzid)
        .hour(i)
        .format('ha'),
    }));
  }

  _mapFormToPurchaseOrder() {
    const result = {};
    // eslint-disable-next-line guard-for-in
    for (const formValue in formValueConfig) {
      const config = formValueConfig[formValue];
      _.assign(result, config.set != null ? config.set.call(this, this[formValue]) : undefined);
    }
    return result;
  }

  isFieldEdited(field) {
    const clean = function(value) {
      if (value == null) {
        return value;
      }
      return JSON.parse(JSON.stringify(value));
    };
    return clean(this._originalFormData[field]) !== clean(this[field]);
  }

  isEdited() {
    const fields = Object.keys(this._originalFormData);
    return _.some(fields, (field) => this.isFieldEdited(field));
  }

  validateField(field) {
    const fieldConfig = formValueConfig[field];
    if (typeof fieldConfig.validate === 'function') {
      return fieldConfig.validate.call(this, this[field]);
    }
    return null;
  }

  save() {
    return this.Promise.try(() => {
      if (this.isEdited()) {
        const purchaseOrder = _.assign(this.purchaseOrder, this._mapFormToPurchaseOrder());
        return purchaseOrder.$update();
      }
      return this.purchaseOrder;
    });
  }

  getLineItemMetaData() {
    const {dropoffDay, dropoffWindow} = this._mapFormToPurchaseOrder();
    return {dropoffDay, dropoffWindow};
  }

  isValid() {
    // eslint-disable-next-line guard-for-in
    for (const formValue in formValueConfig) {
      const config = formValueConfig[formValue];
      const errorMessage =
        config.validate != null ? config.validate.call(this, this[formValue]) : undefined;
      if (errorMessage != null) {
        return false;
      }
    }
    return true;
  }

  setDropoffDateToToday() {
    this.dropoffDate = moment
      .tz(this.foodhub.tzid)
      .startOf('day')
      .toDate();
  }
}
